import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Row, Col, Clearfix, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { find as _find, reduce as _reduce, round as _round, toInteger as _toInteger } from 'lodash';

import { getColor } from 'helpers/charts';

import DashboardWidget12 from 'components/layout/dashboard/DashboardWidget12';

const REACTION_TIME_BOUNDARIES = ['300', '3600', '7200', '21600', '86400', '0', '172800', '+2 days'];
const colors = [
  { r: 0, g: 126, b: 51 }, // 007E33
  { r: 255, g: 136, b: 0 }, // FF8800
  { r: 204, g: 0, b: 0 }, // cc0000
];

class LeadStatsByReactionTime extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedOption: 'employee',
      selectedStatOption: 'count',
    };
  }

  getReactionLabel = reactionTime => {
    const {
      intl: { messages },
    } = this.props;

    if (reactionTime === '+2 days') {
      return messages.gt_2_days;
    }

    switch (_toInteger(reactionTime)) {
      case 0:
        return messages.no_reaction_time;

      case 300:
        return messages.lt_5_minutes;

      case 3600:
        return messages.lt_1_hour;

      case 7200:
        return messages.lt_2_hours;

      case 21600:
        return messages.lt_6_hours;

      case 86400:
        return messages.lt_1_day;

      case 172800:
        return messages.lt_2_days;
    }

    return '?';
  };

  reduceSourceInfos = sourceData => {
    return _reduce(
      sourceData.list,
      (result, value) => {
        result.count += value.count;
        result.won += value.won;
        result.lost += value.lost;
        return result;
      },
      {
        count: 0,
        won: 0,
        lost: 0,
      }
    );
  };

  getContent = () => {
    const {
      intl: { messages },
      data,
    } = this.props;
    const { selectedOption, selectedStatOption } = this.state;
    const source = data[`by_${selectedOption}`];

    const reactionTimeBoundaryCount = REACTION_TIME_BOUNDARIES.length;
    const total = {
      count: 0,
      won: 0,
      lost: 0,
    };
    const countProBoundary = _reduce(
      REACTION_TIME_BOUNDARIES,
      (result, value) => {
        result[value] = {
          count: 0,
          won: 0,
          lost: 0,
        };
        return result;
      },
      {}
    );

    return (
      <>
        <Clearfix className="m-b-10">
          <Col xs={2} className="no-padding bold">
            {messages.client_name}
          </Col>
          <Col xs={1} className="no-padding bold">
            {messages.count}
          </Col>
          <Col xs={1} className="no-padding bold">
            CR
          </Col>
          <Col xs={6} className="no-padding bold">
            {messages.reaction_time}
          </Col>
          <Col xs={1} className="no-padding bold">
            {messages.won}
          </Col>
          <Col xs={1} className="no-padding bold">
            {messages.lost}
          </Col>
        </Clearfix>

        {source.map(sourceData => {
          const sourceTotal = this.reduceSourceInfos(sourceData);
          total.count += sourceTotal.count;
          total.won += sourceTotal.won;
          total.lost += sourceTotal.lost;

          return (
            <Clearfix key={sourceData.id}>
              <Col xs={2} className="no-padding">
                <p className="overflow-ellipsis">{sourceData.name || '?'}</p>
              </Col>
              <Col xs={1} className="no-padding">
                {sourceTotal.count}
              </Col>
              <Col xs={1} className="no-padding">
                {_round((sourceTotal.won / sourceTotal.count) * 100, 1)}%
              </Col>
              <Col xs={6} className="no-padding">
                <Clearfix>
                  {REACTION_TIME_BOUNDARIES.map((reactionTime, reactionTimeBoundaryIndex) => {
                    const label = this.getReactionLabel(reactionTime);
                    const sourceListEntry = _find(sourceData.list, { id: reactionTime }) || {
                      count: 0,
                      won: 0,
                      lost: 0,
                    };
                    const percentage = (sourceListEntry.count / sourceTotal.count) * 100;
                    const cr = (sourceListEntry.won / sourceTotal.count) * 100;

                    countProBoundary[reactionTime].count += sourceListEntry.count;
                    countProBoundary[reactionTime].won += sourceListEntry.won;
                    countProBoundary[reactionTime].lost += sourceListEntry.lost;

                    const style = {
                      background: '#' + getColor(reactionTimeBoundaryIndex + 1, reactionTimeBoundaryCount, colors),
                      width: percentage + '%',
                    };
                    return percentage > 0 ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            <div className="text-left">
                              <b>{label}</b>
                              <br />
                              {messages.count}: {_round(percentage, 2)}%<br />
                              CR: {_round(cr, 2)}%
                            </div>
                          </Tooltip>
                        }
                        key={reactionTime}
                      >
                        <div
                          className="pull-left text-center text-white p-t-5 p-b-5 fs-8"
                          style={style}
                          key={reactionTime}
                        >
                          {selectedStatOption === 'cr' ? _round(cr, 2) : _round(percentage)}%
                        </div>
                      </OverlayTrigger>
                    ) : null;
                  })}
                </Clearfix>
              </Col>
              <Col xs={1} className="p-l-5 p-r-0">
                {sourceTotal.won}
              </Col>
              <Col xs={1} className="p-l-5 p-r-0">
                {sourceTotal.lost}
              </Col>
            </Clearfix>
          );
        })}

        <Clearfix className="m-t-10">
          <Col xs={2} className="no-padding bold">
            TOTAL
          </Col>
          <Col xs={1} className="no-padding bold">
            {total.count}
          </Col>
          <Col xs={1} className="no-padding bold">
            {_round((total.won / total.count) * 100, 1)}%
          </Col>
          <Col xs={6} className="no-padding">
            {REACTION_TIME_BOUNDARIES.map((reactionTime, reactionTimeBoundaryIndex) => {
              const label = this.getReactionLabel(reactionTime);
              const percentage = (countProBoundary[reactionTime].count / total.count) * 100;
              const cr = (countProBoundary[reactionTime].won / total.count) * 100;
              const style = {
                background: '#' + getColor(reactionTimeBoundaryIndex + 1, reactionTimeBoundaryCount, colors),
                color: '#fff',
              };
              return percentage > 0 ? (
                <React.Fragment key={reactionTime}>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        <div className="text-left">
                          <b>{label}</b>
                          <br />
                          {messages.count}: {_round(percentage, 2)}%<br />
                          CR: {_round(cr, 2)}%
                        </div>
                      </Tooltip>
                    }
                  >
                    <Badge style={style} className="fs-8">
                      {label}: {selectedStatOption === 'cr' ? _round(cr, 2) : _round(percentage, 2)}%
                    </Badge>
                  </OverlayTrigger>{' '}
                </React.Fragment>
              ) : null;
            })}
          </Col>
          <Col xs={1} className="no-padding bold">
            {total.won}
          </Col>
          <Col xs={1} className="no-padding bold">
            {total.lost}
          </Col>
        </Clearfix>
      </>
    );
  };

  render() {
    const {
      intl: { messages },
    } = this.props;
    const { selectedOption, selectedStatOption } = this.state;

    const options = [
      { value: 'employee', label: messages.by_employee },
      { value: 'company', label: messages.by_company },
      { value: 'location', label: messages.by_location },
      { value: 'source', label: messages.by_source },
    ];

    const statsOptions = [
      { value: 'count', label: messages.count },
      { value: 'cr', label: messages.sad.conversion_rate },
    ];

    return (
      <Row>
        <Col xs={12}>
          <DashboardWidget12
            title={messages.reaction_time}
            options={options.map(option => {
              const isActive = option.value === selectedOption;

              return (
                <li key={option.value} className={isActive ? 'active ' : ''}>
                  <a
                    href="#changeOption"
                    class={'font-secondary text-master ' + (isActive ? 'bg-master-light ' : '')}
                    onClick={e => {
                      e.preventDefault();
                      this.setState({ selectedOption: option.value });
                    }}
                  >
                    {option.label}
                  </a>
                </li>
              );
            })}
            moreOptions={[
              statsOptions.map(option => {
                const isActive = option.value === selectedStatOption;

                return (
                  <li key={option.value} className={isActive ? 'active ' : ''}>
                    <a
                      href="#changeStatOption"
                      class={'font-secondary text-master ' + (isActive ? 'bg-master-light ' : '')}
                      onClick={e => {
                        e.preventDefault();
                        this.setState({ selectedStatOption: option.value });
                      }}
                    >
                      {option.label}
                    </a>
                  </li>
                );
              }),
            ]}
            content={<div className="p-b-10">{this.getContent()}</div>}
          />
        </Col>
      </Row>
    );
  }
}
LeadStatsByReactionTime.defaultProps = {};

const mapStateToProps = state => {
  return {
    projectConfig: state.data.projectConfig,
  };
};

export default injectIntl(connect(mapStateToProps)(LeadStatsByReactionTime));
